import React, { Suspense, lazy, useEffect, useState, useMemo } from "react";
import { Analytics as DubAnalytics } from "@dub/analytics/react";
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation, Outlet } from "react-router-dom";
import CssBaseline from "@mui/material/CssBaseline";
import { ThemeProvider, createTheme } from "@mui/material/styles";
import { Box, CircularProgress } from "@mui/material";
import i18n, { LIGHT_I18N_PATHS, loadAllTranslations } from "./i18n";
import { getMuiLocaleExtension } from "./utils/muiLocale";
import { QueryClientProvider } from "@tanstack/react-query";
import { AuthProvider } from "./contexts/AuthContext";
import { ThemeProvider as CustomThemeProvider } from "./contexts/ThemeContext";
import { UserProvider } from "./contexts/UserContext";
import { UserPlanProvider } from "./contexts/UserPlanContext";
import { OverdueInvoiceProvider } from "./contexts/OverdueInvoiceContext";
import { useAnalytics } from "./hooks/useAnalytics";
import { useContentsquare } from "./hooks/useContentsquare";
import { usePostHog } from "./hooks/usePostHog";
import ErrorBoundary from "./components/ErrorBoundary";
import ProtectedRoute from "./components/ProtectedRoute";
import OAuthSignupGuard from "./components/OAuthSignupGuard";
import RootRedirect from "./components/RootRedirect";
import WhatsAppFloat from "./components/WhatsAppFloat";
import MauticTracking from "./components/MauticTracking";
import NotificationPermissionPrompt from "./components/NotificationPermissionPrompt";
import ConnectAiPopup from "./components/ConnectAiPopup";
import { usePushSubscription } from "./hooks/usePushSubscription";
import { captureUTMsOnPageLoad } from "./utils/utmCapture";
import { rememberConnectAiOnUtm } from "./utils/connectAiPopup";
import { queryClient } from "./queryClient";

/** Domínio curto Dub para client-side referral (Partners). Referência estável evita re-injetar o script. */
const DUB_ANALYTICS_DOMAINS = { refer: "partners.giobrain.com" };

// Lazy loading de páginas de autenticação
const Acess = lazy(() => import("./pages/acess"));
const Register = lazy(() => import("./pages/register"));
const RegisterLanding = lazy(() => import("./pages/register-landing"));
const RegisterLandingForm = lazy(() => import("./pages/register-landing-form"));
const RegisterLandingVideo = lazy(() => import("./pages/register-landing-video"));
const RegisterLandingSell = lazy(() => import("./pages/register-landing-sell"));
const RegisterLandingNoTrial = lazy(() => import("./pages/register-landing-no-trial"));
const RegisterLandingFormNoTrial = lazy(() => import("./pages/register-landing-form-no-trial"));
const RegisterLandingOffer = lazy(() => import("./pages/register-landing-offer"));
const RegisterLandingOfferForm = lazy(() => import("./pages/register-landing-offer-form"));
const RegisterLandingOfferPrice = lazy(() => import("./pages/register-landing-offer-price"));
const CampaignLanding = lazy(() => import("./pages/campaign-landing"));
const CampaignLandingCountryRedirect = lazy(() => import("./pages/campaign-landing-country-redirect"));
const LeadLanding = lazy(() => import("./pages/lead-landing"));
const RegisterProfile = lazy(() => import("./pages/register-profile"));
const RegisterProfileSell = lazy(() => import("./pages/register-profile-sell"));
const RegisterCompanyFiscal = lazy(() => import("./pages/register-company-fiscal"));
const CreatingAccount = lazy(() => import("./pages/creating-account"));
const CreatingAccountCheckout = lazy(() => import("./pages/creating-account-checkout"));
const AuthCallback = lazy(() => import("./pages/auth-callback"));
// ConfirmEmail removido - login automático direto após cadastro
const ResetPassword = lazy(() => import("./pages/reset-password"));
const OAuthCallback = lazy(() => import("./pages/oauth-callback"));

// Lazy loading de páginas protegidas
const Dashboard = lazy(() => import("./pages/dashboard"));
const Home = lazy(() => import("./pages/home"));
const CriarProjeto = lazy(() => import("./pages/create-project"));
const ProjectDetail = lazy(() => import("./pages/project"));
const ProjectDetails = lazy(() => import("./pages/project-details"));
const CampaignDetail = lazy(() => import("./pages/campaign-detail"));
const CampaignGoogle = lazy(() => import("./pages/campaign-google"));
const AdGroupGoogle = lazy(() => import("./pages/ad-group-google"));
const CampaignMeta = lazy(() => import("./pages/campaign-meta"));
const GoogleAdsPage = lazy(() => import("./pages/googleads"));
const MetaAdsPage = lazy(() => import("./pages/metaads"));
const LinkedInAdsPage = lazy(() => import("./pages/linkedinads"));
const BingAdsPage = lazy(() => import("./pages/bingads"));
const CampaignBing = lazy(() => import("./pages/campaign-bing"));
const MercadoAdsPage = lazy(() => import("./pages/mercadoads"));
const CampaignMercado = lazy(() => import("./pages/campaign-mercado"));
const ProductMercado = lazy(() => import("./pages/product-mercado"));
const SelectPlan = lazy(() => import("./pages/select-plan"));
const OptimizationsPage = lazy(() => import("./pages/optimizations"));
const AlertsPage = lazy(() => import("./pages/alerts"));
const CriativosPage = lazy(() => import("./pages/criativos"));
const FacebookLogin = lazy(() => import("./pages/facebook-login"));
const InstagramLogin = lazy(() => import("./pages/instagram-login"));
const InstagramOrganicLayout = lazy(() => import("./components/InstagramOrganicLayout"));
const InstagramOrganicPage = lazy(() => import("./pages/instagram-organic"));
const InstagramPostsPage = lazy(() => import("./pages/instagram-posts"));
const LinkedInLogin = lazy(() => import("./pages/linkedin-login"));
const TikTokLogin = lazy(() => import("./pages/tiktok-login"));
const SpotifyLogin = lazy(() => import("./pages/spotify-login"));
const BingLogin = lazy(() => import("./pages/bing-login"));
const MercadoLogin = lazy(() => import("./pages/mercado-login"));
const OpenAIAdsPage = lazy(() => import("./pages/openaiads"));
const SpotifyAdsPage = lazy(() => import("./pages/spotifyads"));
const TikTokAdsPage = lazy(() => import("./pages/tiktokads"));
const CampaignOpenAI = lazy(() => import("./pages/campaign-openai"));
const CampaignTikTok = lazy(() => import("./pages/campaign-tiktok"));
const CampaignLinkedIn = lazy(() => import("./pages/campaign-linkedin"));
const CampaignSpotify = lazy(() => import("./pages/campaign-spotify"));
const AdGroupOpenAI = lazy(() => import("./pages/ad-group-openai"));
const AdGroupTikTok = lazy(() => import("./pages/ad-group-tiktok"));
const AdGroupBing = lazy(() => import("./pages/ad-group-bing"));
const AdSetSpotify = lazy(() => import("./pages/ad-set-spotify"));
const AdOpenAI = lazy(() => import("./pages/ad-openai"));
const AdTikTok = lazy(() => import("./pages/ad-tiktok"));
const AdSpotify = lazy(() => import("./pages/ad-spotify"));
const AdBing = lazy(() => import("./pages/ad-bing"));
const AdLinkedIn = lazy(() => import("./pages/ad-linkedin"));
const OpenAILogin = lazy(() => import("./pages/openai-login"));
const SearchConsoleLogin = lazy(() => import("./pages/search-console-login"));
const SearchConsole = lazy(() => import("./pages/search-console"));
const LinkedAccounts = lazy(() => import("./pages/linked-accounts"));
const CreateCampaign = lazy(() => import("./pages/create-campaign"));
const ChatPage = lazy(() => import("./pages/chat"));
const ConversoesCrmPage = lazy(() => import("./pages/conversoes-crm"));
const PublicosPage = lazy(() => import("./pages/publicos"));
const CatalogoMetaPage = lazy(() => import("./pages/catalogo-meta"));
const FunilBuilderPage = lazy(() => import("./pages/funil-builder"));
const MultiClientsPage = lazy(() => import("./pages/multi-clients"));
const MultiClientsAlertasPage = lazy(() => import("./pages/multi-clients-alertas"));
const MultiClientsPortfolioPage = lazy(() => import("./pages/multi-clients-portfolio"));
const MultiClientsRelatoriosPage = lazy(() => import("./pages/multi-clients-relatorios"));
const PartnersPage = lazy(() => import("./pages/partners"));
const AcceptInvitesPage = lazy(() => import("./pages/accept-invites"));
const ProjectWorkspaceLayout = lazy(() => import("./layouts/ProjectWorkspaceLayout"));

function AuthMuiThemeProvider({ children }) {
  const [lang, setLang] = useState(() => i18n.language || "pt-BR");
  useEffect(() => {
    const h = (l) => setLang(l || "pt-BR");
    i18n.on("languageChanged", h);
    setLang(i18n.language || "pt-BR");
    return () => i18n.off("languageChanged", h);
  }, []);
  const theme = useMemo(
    () =>
      createTheme(
        {
          palette: {
            mode: "light",
            primary: {
              main: "#1976d2",
            },
            secondary: {
              main: "#dc004e",
            },
            background: {
              default: "#ffffff",
              paper: "#ffffff",
            },
          },
        },
        getMuiLocaleExtension(lang)
      ),
    [lang]
  );
  return <ThemeProvider theme={theme}>{children}</ThemeProvider>;
}

// Componente de loading para lazy loading
const LoadingFallback = () => (
  <Box
    display="flex"
    justifyContent="center"
    alignItems="center"
    minHeight="100vh"
    sx={{
      background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
    }}
  >
    <CircularProgress size={60} sx={{ color: '#ffffff' }} />
  </Box>
);

// Layout route para rotas protegidas - monta UMA vez e persiste entre navegações.
// Isso evita recriar UserProvider/ThemeProvider/ProtectedRoute a cada troca de página,
// eliminando spinners de loading e chamadas duplicadas ao Supabase.
const ProtectedLayout = () => {
  usePushSubscription();

  return (
    <QueryClientProvider client={queryClient}>
      <CustomThemeProvider>
        <UserProvider>
          <UserPlanProvider>
            <OverdueInvoiceProvider>
            <ProtectedRoute>
              <OAuthSignupGuard>
                <Suspense fallback={<LoadingFallback />}>
                  <NotificationPermissionPrompt />
                  <ConnectAiPopup />
                  <Outlet />
                </Suspense>
              </OAuthSignupGuard>
            </ProtectedRoute>
            </OverdueInvoiceProvider>
          </UserPlanProvider>
        </UserProvider>
      </CustomThemeProvider>
    </QueryClientProvider>
  );
};

// Componente wrapper para paginas de autenticacao com tema padrao
// Removido CssBaseline duplicado - agora esta apenas no nivel do App
const AuthRoute = ({ children }) => {
  return (
    <AuthMuiThemeProvider>
      <Suspense fallback={<LoadingFallback />}>
        {children}
      </Suspense>
    </AuthMuiThemeProvider>
  );
};

// Componente interno do App que usa o hook de analytics
const AppContent = () => {
  const location = useLocation();
  
  // Inicializa o analytics para rastrear mudanças de página
  useAnalytics();

  // Contentsquare/Hotjar UXA nas LPs. O onboarding do projeto liga o mesmo script só nas etapas.
  useContentsquare();

  // PostHog (replay + heatmaps) nas mesmas rotas, em paralelo ao Hotjar.
  usePostHog();

  // Saiu da landing (ex.: redirect de quem já está logado): garante as traduções completas.
  useEffect(() => {
    if (!LIGHT_I18N_PATHS.includes(location.pathname)) {
      loadAllTranslations().catch(() => {});
    }
  }, [location.pathname]);

  // Captura UTMs da URL em toda navegação (página + parâmetros)
  // Isso garante que UTMs sejam capturados mesmo em cenários de navegação complexos
  useEffect(() => {
    captureUTMsOnPageLoad();
    rememberConnectAiOnUtm(location.search);
    
    // Debug: mostrar UTMs salvos no console
    const savedUtm = localStorage.getItem('gio_utm_data');
    if (savedUtm) {
      try {
        const parsed = JSON.parse(savedUtm);
        if (parsed.utm_source || parsed.gclid || parsed.fbclid) {
          console.log('📊 UTM Tracking ativo:', {
            page: location.pathname,
            utm_source: parsed.utm_source,
            utm_campaign: parsed.utm_campaign,
            gclid: parsed.gclid ? '✅' : '❌',
            fbclid: parsed.fbclid ? '✅' : '❌',
          });
        }
      } catch (e) {
        // Ignore parse errors
      }
    }
  }, [location.pathname, location.search]); // Re-executa em cada navegação E mudança de params
  
  return (
    <>
      <MauticTracking />
      <Routes>
      {/* Rotas de autenticacao - COM tema padrao claro */}
      <Route path="/" element={<RootRedirect />} />
      <Route path="/acess" element={<AuthRoute><Acess /></AuthRoute>} />
      <Route path="/auth/callback" element={<AuthRoute><AuthCallback /></AuthRoute>} />
      <Route path="/register" element={<AuthRoute><Register /></AuthRoute>} />
      <Route path="/register-landing" element={<AuthRoute><RegisterLanding /></AuthRoute>} />
      <Route path="/register-landing-form" element={<AuthRoute><RegisterLandingForm /></AuthRoute>} />
      <Route path="/register-landing-video" element={<AuthRoute><RegisterLandingVideo /></AuthRoute>} />
      <Route path="/register-landing-sell" element={<AuthRoute><RegisterLandingSell /></AuthRoute>} />
      <Route path="/register-landing-no-trial" element={<AuthRoute><RegisterLandingNoTrial /></AuthRoute>} />
      <Route path="/register-landing-form-no-trial" element={<AuthRoute><RegisterLandingFormNoTrial /></AuthRoute>} />
      <Route path="/register-landing-offer" element={<AuthRoute><RegisterLandingOffer /></AuthRoute>} />
      <Route path="/register-landing-offer-form" element={<AuthRoute><RegisterLandingOfferForm /></AuthRoute>} />
      <Route path="/register-landing-offer-price" element={<AuthRoute><RegisterLandingOfferPrice /></AuthRoute>} />
      <Route path="/lp/:type/:country" element={<AuthRoute><CampaignLanding /></AuthRoute>} />
      <Route path="/lp/:country" element={<AuthRoute><CampaignLandingCountryRedirect /></AuthRoute>} />
      <Route path="/lead" element={<AuthRoute><LeadLanding /></AuthRoute>} />
      <Route path="/register-profile" element={<AuthRoute><RegisterProfile /></AuthRoute>} />
      <Route path="/register-profile-sell" element={<AuthRoute><RegisterProfileSell /></AuthRoute>} />
      <Route path="/register-company" element={<Navigate to="/register" replace />} />
      <Route path="/register-company-fiscal" element={<AuthRoute><RegisterCompanyFiscal /></AuthRoute>} />
      <Route path="/creating-account" element={<AuthRoute><CreatingAccount /></AuthRoute>} />
      <Route path="/creating-account-checkout" element={<AuthRoute><CreatingAccountCheckout /></AuthRoute>} />
      {/* Rota /confirm-email removida - login automático direto após cadastro */}
      <Route path="/reset-password" element={<AuthRoute><ResetPassword /></AuthRoute>} />
      <Route path="/oauth/callback" element={<AuthRoute><OAuthCallback /></AuthRoute>} />
      <Route path="/callback" element={<AuthRoute><OAuthCallback /></AuthRoute>} />
      
      {/* Rotas protegidas - layout único que persiste entre navegações */}
      <Route element={<ProtectedLayout />}>
        <Route path="/dashboard" element={<Dashboard />} />
        <Route path="/home" element={<Home />} />
        <Route path="/create-project" element={<CriarProjeto />} />
        <Route path="/campaigns/:id" element={<CampaignDetail />} />
        <Route path="/campaign-google/:accountId/:campaignId" element={<CampaignGoogle />} />
        <Route path="/ad-group-google/:accountId/:campaignId/:adGroupId" element={<AdGroupGoogle />} />
        <Route path="/campaign-meta/:accountId/:campaignId" element={<CampaignMeta />} />
        <Route path="/campaign-bing/:accountId/:campaignId" element={<CampaignBing />} />
        <Route path="/campaign-mercado/:accountId/:campaignId" element={<CampaignMercado />} />
        <Route path="/product-mercado/:accountId/:itemId" element={<ProductMercado />} />
        <Route path="/campaign-openai/:accountId/:campaignId" element={<CampaignOpenAI />} />
        <Route path="/campaign-tiktok/:accountId/:campaignId" element={<CampaignTikTok />} />
        <Route path="/campaign-linkedin/:accountId/:campaignId" element={<CampaignLinkedIn />} />
        <Route path="/campaign-spotify/:accountId/:campaignId" element={<CampaignSpotify />} />
        <Route path="/ad-group-tiktok/:accountId/:adGroupId" element={<AdGroupTikTok />} />
        <Route path="/ad-group-bing/:accountId/:adGroupId" element={<AdGroupBing />} />
        <Route path="/ad-set-spotify/:accountId/:adSetId" element={<AdSetSpotify />} />
        <Route path="/ad-group-openai/:accountId/:adGroupId" element={<AdGroupOpenAI />} />
        <Route path="/ad-openai/:accountId/:adId" element={<AdOpenAI />} />
        <Route path="/ad-tiktok/:accountId/:adId" element={<AdTikTok />} />
        <Route path="/ad-spotify/:accountId/:adId" element={<AdSpotify />} />
        <Route path="/ad-bing/:accountId/:adId" element={<AdBing />} />
        <Route path="/ad-linkedin/:accountId/:adId" element={<AdLinkedIn />} />
        <Route path="/select-plan" element={<SelectPlan />} />
        <Route path="/facebook-login/:id?" element={<FacebookLogin />} />
        <Route path="/instagram-login/:id?" element={<InstagramLogin />} />
        <Route path="/linkedin-login/:id?" element={<LinkedInLogin />} />
        <Route path="/tiktok-login/:id?" element={<TikTokLogin />} />
        <Route path="/spotify-login/:id?" element={<SpotifyLogin />} />
        <Route path="/bing-login/:id?" element={<BingLogin />} />
        <Route path="/mercado-login/:id?" element={<MercadoLogin />} />
        <Route path="/openai-login/:id?" element={<OpenAILogin />} />
        <Route path="/search-console-login/:id?" element={<SearchConsoleLogin />} />
        <Route path="/multi-clients" element={<MultiClientsPage />} />
        <Route path="/invites" element={<AcceptInvitesPage />} />
        <Route path="/multi-clients/alertas" element={<MultiClientsAlertasPage />} />
        <Route path="/multi-clients/portfolio" element={<MultiClientsPortfolioPage />} />
        <Route path="/multi-clients/relatorios" element={<MultiClientsRelatoriosPage />} />
        <Route path="/multi-clients/configuracoes" element={<Navigate to="/multi-clients" replace />} />

        {/* Shell persistente do projeto (Sidebar + Header + bootstrap) */}
        <Route path="/projects/:projectId" element={<ProjectWorkspaceLayout />}>
          <Route index element={<ProjectDetail />} />
          <Route path="details" element={<ProjectDetails />} />
          <Route path="optimizations" element={<OptimizationsPage />} />
          <Route path="alerts" element={<AlertsPage />} />
          <Route path="criativos" element={<CriativosPage />} />
          <Route path="search-console" element={<SearchConsole />} />
          <Route path="instagram" element={<InstagramOrganicLayout />}>
            <Route index element={<InstagramOrganicPage />} />
            <Route path="calendario" element={<Navigate to=".." relative="path" replace />} />
            <Route path="publicacoes" element={<InstagramPostsPage />} />
          </Route>
          <Route path="create-campaign" element={<CreateCampaign />} />
          <Route path="chat" element={<ChatPage />} />
          <Route path="conversoes-crm" element={<ConversoesCrmPage />} />
          <Route path="publicos" element={<PublicosPage />} />
          <Route path="catalogo-meta" element={<CatalogoMetaPage />} />
          <Route path="funil-builder" element={<FunilBuilderPage />} />
          <Route path="partners" element={<PartnersPage />} />
          <Route path="multi-clients" element={<Navigate to="/multi-clients" replace />} />
        </Route>
        <Route path="/googleads/:projectId" element={<ProjectWorkspaceLayout />}>
          <Route index element={<GoogleAdsPage />} />
        </Route>
        <Route path="/metaads/:projectId" element={<ProjectWorkspaceLayout />}>
          <Route index element={<MetaAdsPage />} />
        </Route>
        <Route path="/linkedinads/:projectId" element={<ProjectWorkspaceLayout />}>
          <Route index element={<LinkedInAdsPage />} />
        </Route>
        <Route path="/bingads/:projectId" element={<ProjectWorkspaceLayout />}>
          <Route index element={<BingAdsPage />} />
        </Route>
        <Route path="/mercadoads/:projectId" element={<ProjectWorkspaceLayout />}>
          <Route index element={<MercadoAdsPage />} />
        </Route>
        <Route path="/openaiads/:projectId" element={<ProjectWorkspaceLayout />}>
          <Route index element={<OpenAIAdsPage />} />
        </Route>
        <Route path="/spotifyads/:projectId" element={<ProjectWorkspaceLayout />}>
          <Route index element={<SpotifyAdsPage />} />
        </Route>
        <Route path="/tiktokads/:projectId" element={<ProjectWorkspaceLayout />}>
          <Route index element={<TikTokAdsPage />} />
        </Route>
        <Route path="/linked-accounts/:projectId" element={<ProjectWorkspaceLayout />}>
          <Route index element={<LinkedAccounts />} />
        </Route>
      </Route>

      {/* Rotas inexistentes redirecionam para a página de acesso */}
      <Route path="*" element={<Navigate to="/acess" replace />} />
    </Routes>
    </>
  );
};

const WhatsAppFloatWrapper = () => {
  return (
    <AuthMuiThemeProvider>
      <WhatsAppFloat />
    </AuthMuiThemeProvider>
  );
};

function App() {
  return (
    <ErrorBoundary>
      <CssBaseline />
      <Router>
        <DubAnalytics domainsConfig={DUB_ANALYTICS_DOMAINS} />
        <AuthProvider>
          <AppContent />
          <WhatsAppFloatWrapper />
        </AuthProvider>
      </Router>
    </ErrorBoundary>
  );
}

export default App;
